import { areDeviceSessionsEqual } from "~/utilities/deviceLimit"

export interface DeviceSession {
  deviceId: string
  lastActive: string
  deviceInfo: string
}

const POLLING_INTERVAL = 3 * 60 * 1000 // 3 minutes
const SESSION_EXPIRY = 6 * 24 * 60 * 60 * 1000 // 6 days

export function useDeviceLimiter() {
  const deviceKey = useDeviceKey()

  const currentDevice = ref<DeviceSession | null>(null)
  const hasActiveDeviceConflict = ref(false)
  let deviceCheckInterval: ReturnType<typeof setInterval> | null =
    null

  function getDeviceIdentifier(): string {
    if (import.meta.server)
      return deviceKey.value

    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.hardwareConcurrency,
      deviceKey.value,
    ]

    return components.join("|")
  }

  const origin = useCurrentOrigin()

  function getDeviceInfo(): string {
    return `${navigator.userAgent} - ${origin}`
  }

  function isDeviceSessionExpired(session: DeviceSession): boolean {
    const lastActiveTime = new Date(session.lastActive).getTime()
    const now = new Date().getTime()
    return now - lastActiveTime > SESSION_EXPIRY
  }

  const { get: getActiveDevice, set: setActiveDevice } = useUserMetadata<DeviceSession>("active-device")

  async function checkActiveDevice(): Promise<
    DeviceSession | undefined
  > {
    try {
      const deviceSession = await getActiveDevice()

      if (!deviceSession)
        return undefined

      if (isDeviceSessionExpired(deviceSession)) {
        currentDevice.value = null
        hasActiveDeviceConflict.value = false
        return undefined
      }

      const thisDeviceId = getDeviceIdentifier()
      const isConflicting = !areDeviceSessionsEqual(deviceSession.deviceId, thisDeviceId)

      if (isConflicting) {
        const win = window as any
        const pendoAnalytics = win.pendo
        pendoAnalytics?.track?.("Device Limiter Conflict", {
          deviceId: deviceSession.deviceId,
          currentDeviceId: thisDeviceId,
        })
      }
      hasActiveDeviceConflict.value = isConflicting
      currentDevice.value = deviceSession

      return isConflicting ? deviceSession : undefined
    } catch (error) {
      if (!isUnauthorized(error)) {
        captureException(error)
      }

      return undefined
    }
  }

  async function registerDevice(userId: string): Promise<void> {
    try {
      const deviceSession: DeviceSession = {
        deviceId: getDeviceIdentifier(),
        lastActive: new Date().toISOString(),
        deviceInfo: getDeviceInfo(),
      }
      await setActiveDevice(deviceSession, userId)
      currentDevice.value = deviceSession
      hasActiveDeviceConflict.value = false
    } catch (error) {
      if (!isUnauthorized(error)) {
        captureException(error)
      }

      // If metadata fails, still allow access but don't update state
      currentDevice.value = null
      hasActiveDeviceConflict.value = false
    }
  }

  async function startDevicePolling() {
    if (deviceCheckInterval) {
      clearInterval(deviceCheckInterval)
    }

    deviceCheckInterval = setInterval(async () => {
      await checkActiveDevice()
    }, POLLING_INTERVAL)
  }

  async function initializeDeviceLimiter(
    userId: string,
  ): Promise<void> {
    try {
      await startDevicePolling()
      const existingDevice = await checkActiveDevice()

      if (!existingDevice) {
        await registerDevice(userId)
      }
    } catch (error) {
      if (!isUnauthorized(error)) {
        captureException(error)
      }

      // If anything fails, don't block access
      currentDevice.value = null
      hasActiveDeviceConflict.value = false
    }
  }

  async function terminateDeviceSession(
    userId?: string,
  ): Promise<void> {
    try {
      await setActiveDevice(null, userId)
      currentDevice.value = null
      hasActiveDeviceConflict.value = false
      if (deviceCheckInterval) {
        clearInterval(deviceCheckInterval)
        deviceCheckInterval = null
      }
    } catch (error) {
      if (!isUnauthorized(error)) {
        captureException(error)
      }
    }
  }

  return {
    currentDevice,
    hasActiveDeviceConflict,
    initializeDeviceLimiter,
    registerDevice,
    terminateDeviceSession,
  }
}
